/**
 * Data layer for the console. Everything on screen that claims to be real
 * comes through here.
 *
 * The session is the one the rest of the product already issues: an HttpOnly
 * cookie plus a double-submit CSRF token. Nothing here mints, stores, or
 * inspects a credential — it reuses `../lib/api.js` and `../lib/csrf.js` so a
 * user signed in on one shell is signed in on the other, and a session
 * revoked anywhere is revoked here too.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { normalizeActionEnvelope } from "./action-envelope.js";
import {
  DEFAULT_API_TIMEOUT_MS,
  apiBase,
  fetchWithDeadline,
  _isRetryableHttpStatus,
  _isTransientFetchError,
  _isWriteMethod,
  _sleep,
} from "../lib/api.js";
import {
  CSRF_HEADER_NAME,
  _isCsrfRejection,
  _readCsrfCookie,
  getCsrfToken,
  refreshCsrfToken,
  setCsrfToken,
} from "../lib/csrf.js";
import { bindContractApi, buildContractPath } from "../lib/api/contract-client.js";

/** Where an ended session lands. The console mounts sign-in under /auth. */
export const SIGN_IN_HASH = "#/auth/login";

/** A cookie session is present when the server has handed us a CSRF token. */
export function hasSession() {
  return !!(getCsrfToken() || _readCsrfCookie());
}

/**
 * Endpoints that answer 401 for bad credentials / missing identity — never an
 * end-of-session. expiring here rewrote "invalid credentials" into a generic
 * "session ended" message and cleared CSRF while the operator was still on
 * Sign in, so the form looked broken even when the API was healthy.
 */
function isAuthCredentialPath(path) {
  const p = String(path || "").split("?")[0];
  return (
    p === "/auth/login" ||
    p === "/auth/login/email-second" ||
    p === "/auth/signup/start" ||
    p === "/auth/signup/verify" ||
    p === "/auth/activate" ||
    p === "/auth/forgot/email/check" ||
    p === "/auth/forgot/email/start" ||
    p === "/auth/forgot/email/complete" ||
    p === "/auth/forgot/start" ||
    p === "/auth/forgot/complete" ||
    p === "/auth/code/resend"
  );
}

let expiring = null;

/**
 * One teardown even when a screenful of parallel reads all come back 401.
 *
 * This deliberately does NOT call /auth/logout. That deletes the cookie
 * server-side, which made every 401 unrecoverable — including the ones a
 * control-plane hiccup used to produce. The server has already refused this
 * token, so there is nothing to clean up; if the session is genuinely revoked
 * the cookie is inert, and if the 401 was spurious the operator gets their
 * session back by signing in without a destroyed jar behind them.
 */
function expire() {
  if (!expiring) {
    expiring = (async () => {
      try {
        setCsrfToken("");
        if (location.hash !== SIGN_IN_HASH) location.hash = SIGN_IN_HASH;
      } finally {
        expiring = null;
      }
    })();
  }
  return expiring;
}

/**
 * One request. Reads retry transient failures; writes do not, except for a
 * single CSRF refresh — a rotated token is the one write failure that is safe
 * to replay, because the request never reached the handler.
 */
export async function api(path, opts) {
  opts = opts || {};
  const method = String(opts.method || "GET").toUpperCase();
  const headers = Object.assign({}, opts.headers || {});
  let body = opts.body;
  if (body && typeof body === "object" && !(body instanceof FormData)) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(body);
  }
  /* QUALITY-1: freeze writes while the browser reports offline (UX gate). */
  if (_isWriteMethod(method)) {
    try {
      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        throw new Error("You are offline. Changes stay paused until the connection returns.");
      }
    } catch (e) {
      if (String((e && e.message) || "").indexOf("offline") >= 0) throw e;
    }
  }
  if (_isWriteMethod(method) && !headers[CSRF_HEADER_NAME]) {
    const t = getCsrfToken();
    if (t) headers[CSRF_HEADER_NAME] = t;
  }

  const isRead = method === "GET" || method === "HEAD";
  const retries = Number.isFinite(Number(opts.retries))
    ? Math.max(0, Number(opts.retries))
    : (isRead ? 2 : 0);
  let csrfRetried = false;
  let lastErr;
  const authCred = isAuthCredentialPath(path);

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const r = await fetchWithDeadline(
        apiBase() + path,
        { method, headers, body, signal: opts.signal },
        opts.timeoutMs != null ? opts.timeoutMs : DEFAULT_API_TIMEOUT_MS,
      );
      if (r.status === 401) {
        /* Credential screens must surface the server body (invalid credentials).
           Intentional logout passes skipExpire so expire() does not race Sign out. */
        if (authCred || opts.skipExpire) {
          throw new Error(await readError(r));
        }
        await expire();
        throw new Error("Your session ended. Sign in again.");
      }
      if (!r.ok) {
        if (_isWriteMethod(method) && !csrfRetried && r.status === 403) {
          const t = await r.clone().text().catch(() => "");
          if (_isCsrfRejection(403, t)) {
            csrfRetried = true;
            const fresh = await refreshCsrfToken();
            if (fresh) {
              headers[CSRF_HEADER_NAME] = fresh;
              attempt--;
              continue;
            }
          }
        }
        if (isRead && attempt < retries && _isRetryableHttpStatus(r.status)) {
          await _sleep(250 * (2 ** attempt));
          continue;
        }
        throw new Error(await readError(r));
      }
      if (opts.raw) return r;
      const ct = r.headers.get("content-type") || "";
      return ct.includes("application/json") ? r.json() : r.text();
    } catch (e) {
      lastErr = e;
      if (isRead && attempt < retries && _isTransientFetchError(e)) {
        await _sleep(250 * (2 ** attempt));
        continue;
      }
      throw e;
    }
  }
  throw lastErr || new Error("The request did not complete.");
}

/** Server errors arrive in several shapes; operators only need the sentence. */
async function readError(r) {
  const raw = await r.text().catch(() => "");
  let detail = raw;
  try {
    const j = JSON.parse(raw);
    const d = j && (j.detail != null ? j.detail : j.message);
    if (typeof d === "string") detail = d;
    else if (Array.isArray(d)) detail = d.map((x) => (x && x.msg) || String(x)).join("; ");
    else if (d && typeof d === "object") detail = d.msg || JSON.stringify(d);
  } catch (_) { /* plain text */ }
  return `${r.status} ${detail || r.statusText}`.trim();
}

bindContractApi(api);

/**
 * Address an endpoint by its contract operation id rather than by a literal
 * path. The manifest is the source of truth for both sides, so a route that
 * moves breaks the build instead of breaking a screen in front of an operator.
 *
 *   path("get__devices", { query:{ limit: 50 } })
 *   path("get__devices_{device_id}", { params:{ device_id: id } })
 */
export function path(operationId, options) {
  return buildContractPath(operationId, options || {});
}

/** For panels where "not available to you" is an answer, not a failure. */
export async function apiOr(path, fallback, opts) {
  try {
    return await api(path, opts);
  } catch (e) {
    return typeof fallback === "function" ? fallback(e) : fallback;
  }
}

/* ── hooks ───────────────────────────────────────────────────────────── */

/**
 * Read one path. Returns the four things a panel has to be able to draw:
 * what came back, what went wrong, whether we are still waiting, and how to
 * ask again.
 *
 * `path` of null means "not yet" — a detail panel with no selection should
 * not fire a request for it.
 *
 * `loading` is the first read only. A re-read over data already on screen
 * reports `refreshing` instead, so a polled panel does not throw its content
 * away and flash a spinner every few seconds.
 */
export function useApi(path, opts) {
  const [state, setState] = useState({ data: null, error: "", loading: !!path, refreshing: false });
  const [nonce, setNonce] = useState(0);
  const o = useRef(opts);
  o.current = opts;

  useEffect(() => {
    if (!path) {
      setState({ data: null, error: "", loading: false, refreshing: false });
      return undefined;
    }
    const ac = new AbortController();
    let live = true;
    setState((s) => ({ data: s.data, error: "", loading: s.data == null, refreshing: s.data != null }));
    api(path, Object.assign({}, o.current, { signal: ac.signal }))
      .then((data) => { if (live) setState({ data, error: "", loading: false, refreshing: false }); })
      .catch((e) => {
        if (!live || ac.signal.aborted) return;
        // A failed refresh keeps the last good answer on screen next to the
        // error. Blanking a fleet because one poll tick timed out is worse
        // than showing an operator something a few seconds stale.
        setState((s) => ({
          data: s.data, error: String((e && e.message) || e), loading: false, refreshing: false,
        }));
      });
    return () => { live = false; ac.abort(); };
  }, [path, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { ...state, reload };
}

/**
 * Same, but re-reads on a timer. Used where the screen claims to be live and
 * has to earn it. Polling pauses while the tab is hidden — a backgrounded
 * console should not keep a fleet-wide query running every few seconds.
 */
export function usePolled(path, everyMs, opts) {
  const r = useApi(path, opts);
  const reload = r.reload;
  const err = r.error;
  useEffect(() => {
    if (!path || !everyMs) return undefined;
    let t = 0;
    const tick = () => {
      if (!document.hidden) reload();
      /* Soft≠Live: 429 means Live gate throttled — back off; keep last good data. */
      const throttled = /\b429\b|too many/.test(String(err || "").toLowerCase());
      const wait = throttled ? Math.max(Number(everyMs) || 0, 90000) : everyMs;
      t = setTimeout(tick, wait);
    };
    t = setTimeout(tick, everyMs);
    return () => clearTimeout(t);
  }, [path, everyMs, reload, err]);
  return r;
}

/**
 * A write bound to a button. `run` never throws — a failed action leaves its
 * message on the button's own panel rather than unmounting the screen.
 * P0-8b: also exposes ``envelope`` / ``envelopeOf`` (intent→admission→receipt→proof).
 */
export function useAction() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [envelope, setEnvelope] = useState(null);
  const errRef = useRef("");
  const envRef = useRef(null);
  const run = useCallback(async (path, opts) => {
    setBusy(true);
    setError("");
    errRef.current = "";
    const actorKind = (opts && opts.actor_kind) || "Human";
    try {
      const data = await api(path, Object.assign({ method: "POST" }, opts || {}));
      const env = normalizeActionEnvelope(data, { actor_kind: actorKind });
      envRef.current = env;
      setEnvelope(env);
      return data;
    } catch (e) {
      const msg = String((e && e.message) || e);
      errRef.current = msg;
      setError(msg);
      const env = normalizeActionEnvelope(null, { actor_kind: actorKind, error: msg });
      envRef.current = env;
      setEnvelope(env);
      return null;
    } finally {
      setBusy(false);
    }
  }, []);
  return {
    run,
    busy,
    error,
    envelope,
    /** Sync read of last failure — Soft≠Live: use after `run` returns null (React state lags). */
    errorOf: () => errRef.current,
    /** Sync read of last Action Result Envelope. */
    envelopeOf: () => envRef.current,
    clear: () => {
      setError("");
      errRef.current = "";
      setEnvelope(null);
      envRef.current = null;
    },
  };
}

export { apiBase, refreshCsrfToken };
